import { CloseOutlined, SearchOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client';
import {
  Col,
  Empty,
  Icon,
  Popconfirm,
  Progress,
  Row,
  Select,
  Spin,
  Statistic,
  Table,
  message,
  Tabs,
} from 'antd';
import classNames from 'classnames';
import cookies from 'js-cookie';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TableAsyncSelect } from 'common/components/table-filter-component';
import { cookieNames } from '~client/constants';
import { formatPriceWithNoSymbol, formatPriceWithSpace } from '~helpers/currency';
import { formatDay } from '~helpers/day';
import {
  MUTATION_AUDIT_PAYOUT,
  MUTATION_MANUALLY_SETTLE_PAYOUT,
  MUTATION_SYNC_XHJ_TOPOP,
  QUERY_BALANCE_COUNTRY_CURRENCIES,
  QUERY_PARTNER_PAYOUT,
  QUERY_SEARCH_PARTNER_CONTACTS,
  QUERY_SETTLEMENT_INFO,
} from '~pages/dashboard/referral/payout/queries';

import { PayoutSettlementSync as PayoutSettlementSyncIcon } from 'common/components/svgs';
import { Statuses } from './list';

const { TabPane } = Tabs;

export const PaymentPreferenceConfig = {
  CHN: ['ONLINE', 'OFFLINE'],
  IND: ['OFFLINE'],
};
export const PaymentPreferenceAction = countryCode => {
  return countryCode === 'CHN' ? ['ONLINE', 'OFFLINE'] : ['OFFLINE'];
};

const SettlementList = props => {
  const { t } = useTranslation();
  const { onClose, visiable, role } = props;

  const [isVisitable, setIsVisitable] = useState(visiable);
  const [selectedPaymentPreference, setSelectedPaymentPreference] = useState('ONLINE');
  const [selectWalletCountry, setSelectWalletCountry] = useState();
  const [selectWalletCurrency, setSelectWalletCurrency] = useState();
  const [selectedRows, setSelectedRows] = useState([]);
  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 10,
  });

  const [queryFilter, setQueryFilter] = useState({
    partnerId: [],
  });

  const [showPartnerIdFilter, setShowPartnerIdFilter] = useState(false);
  const [searchPartnerName, setSearchPartnerName] = useState(null);

  const {
    loading,
    data: payoutListResponse,
    refetch: refetchPartnerList,
  } = useQuery(QUERY_PARTNER_PAYOUT, {
    variables: {
      pageNumber: pagination.pageNumber,
      pageSize: pagination.pageSize,
      partnerIds: queryFilter.partnerId.length ? queryFilter.partnerId : null,
      sortBy: 'UPDATED_AT',
      sortDirection: 'DESCENDING',
      statuses: [Statuses.Approved],
      currencies: [selectWalletCurrency],
      countryIds: [selectWalletCountry],
      paymentPreference: selectedPaymentPreference,
    },
    skip: !selectWalletCurrency || !selectWalletCountry,
    fetchPolicy: 'cache-and-network',
    onError: e => {
      message.error(e?.toString());
    },
  });

  const { loading: partnerListLoading, data: partnerListResponse } = useQuery(
    QUERY_SEARCH_PARTNER_CONTACTS,
    {
      variables: {
        partnerName: searchPartnerName || null,
      },
    },
  );

  const { data: settlementInfoResponse, loading: settlementInfoLoading } = useQuery(
    QUERY_SETTLEMENT_INFO,
    {
      variables: {
        currency: selectWalletCurrency,
        countryId: selectWalletCountry,
        paymentPreference: selectedPaymentPreference,
      },
      onError: e => {
        message.error(e.message);
        setSelectWalletCountry();
        setSelectWalletCurrency();
      },
      skip: !selectWalletCurrency || !selectWalletCountry,
      fetchPolicy: 'no-cache',
    },
  );

  const { data: balanceCountryCurrencies } = useQuery(QUERY_BALANCE_COUNTRY_CURRENCIES, {
    fetchPolicy: 'no-cache',
  });

  const [removeFromSettlementList, { loading: removeFromSettlementListLoading }] = useMutation(
    MUTATION_AUDIT_PAYOUT,
    {
      variables: {
        operationType: 'UNAPPROVE',
      },
      refetchQueries: [QUERY_PARTNER_PAYOUT, QUERY_SETTLEMENT_INFO],
      onError: e => {
        message.error(e.message);
      },
    },
  );

  const [syncXHJTopop, { loading: syncXHJTopopLoading }] = useMutation(MUTATION_SYNC_XHJ_TOPOP, {
    refetchQueries: [QUERY_SETTLEMENT_INFO, QUERY_PARTNER_PAYOUT],
    onError: e => {
      message.error(e.message);
    },
  });

  const [manuallySettlePayout] = useMutation(MUTATION_MANUALLY_SETTLE_PAYOUT, {
    refetchQueries: [QUERY_SETTLEMENT_INFO, QUERY_PARTNER_PAYOUT],
    onError: e => {
      message.error(e.message);
    },
  });

  const settlementInfo = useMemo(() => {
    return settlementInfoResponse?.getCompanyWalletBalance;
  }, [settlementInfoResponse]);

  const availableCountries = useMemo(() => {
    const calcAvailableCountries =
      balanceCountryCurrencies?.getBalanceCountryCurrencies?.balanceCountryCurrencies?.map(
        edge => ({
          label: edge.country?.name || '',
          value: edge.countryId,
          currencies: edge.currency,
          countryCode: edge.country?.countryCode,
        }),
      ) || [];
    console.log(calcAvailableCountries);
    if (!selectWalletCountry && calcAvailableCountries.length) {
      const lastTimeSavedSelectedCountry = cookies.get(cookieNames.payoutDefaultSettlementCountry);
      if (lastTimeSavedSelectedCountry) {
        const selectedCountry = calcAvailableCountries.find(
          country => country.value === lastTimeSavedSelectedCountry,
        );
        setSelectWalletCountry(lastTimeSavedSelectedCountry);
        setSelectedPaymentPreference(PaymentPreferenceAction(selectedCountry.countryCode)?.[0]);
      } else {
        const defaultCountry = calcAvailableCountries.find(country => country.label === 'China');
        const defaultCountryValue = defaultCountry?.value || calcAvailableCountries[0].value;
        setSelectWalletCountry(defaultCountryValue);
        setSelectedPaymentPreference(
          PaymentPreferenceAction(defaultCountry.countryCode)?.[0] ||
            PaymentPreferenceAction(calcAvailableCountries[0].countryCode)?.[0],
        );

        cookies.set(cookieNames.payoutDefaultSettlementCountry, defaultCountryValue);
      }
    }

    return calcAvailableCountries;
  }, [balanceCountryCurrencies]);

  const availableCurrencies = useMemo(() => {
    const calcAvailableCurrencies =
      availableCountries
        ?.find(country => country.value === selectWalletCountry)
        ?.currencies.map(c => ({ label: c, value: c })) || [];

    if (!selectWalletCurrency && calcAvailableCurrencies.length) {
      const lastTimeSavedSelectedCurrency = cookies.get(
        cookieNames.payoutDefaultSettlementCurrency,
      );
      if (lastTimeSavedSelectedCurrency) {
        setSelectWalletCurrency(lastTimeSavedSelectedCurrency);
      } else {
        const defaultCurrency =
          calcAvailableCurrencies.find(c => c === 'CNY') || calcAvailableCurrencies[0].value;

        setSelectWalletCurrency(defaultCurrency);
        cookies.set(cookieNames.payoutDefaultSettlementCurrency, calcAvailableCurrencies[0].value);
      }
    }

    return calcAvailableCurrencies;
  }, [availableCountries, selectWalletCountry]);

  const currentSelectCountry = availableCountries.find(
    country => country.value === selectWalletCountry,
  );

  const isAutoSettlementCountry =
    ['China'].includes(currentSelectCountry?.label) && selectedPaymentPreference !== 'OFFLINE';

  const partnerList = useMemo(() => {
    return (
      partnerListResponse?.partnerContacts?.edges.map(edge => ({
        label: edge.node.partner.name,
        value: edge.node.partner.id,
      })) || []
    );
  }, [partnerListResponse]);

  const payoutList = useMemo(() => {
    return payoutListResponse?.listPartnerPayoutRecords?.edges.map(edge => edge.node) || [];
  }, [payoutListResponse]);

  const payoutListTotalCount = useMemo(() => {
    return payoutListResponse?.listPartnerPayoutRecords?.totalCount || 0;
  }, [payoutListResponse]);

  const totalPages = useMemo(() => {
    return payoutListTotalCount / pagination.pageSize;
  }, [pagination.pageSize, payoutListTotalCount]);

  const isValidFilter = useMemo(() => {
    return !!queryFilter.partnerId.length;
  }, [queryFilter]);

  const handleRowSelect = rows => {
    setSelectedRows(rows);
  };

  const refetchAndResetPagination = async () => {
    const refechPayoutListResponse = await refetchPartnerList();
    if (refechPayoutListResponse.data) {
      const listPartnerPayoutRecords = refechPayoutListResponse.data.listPartnerPayoutRecords;
      if (
        listPartnerPayoutRecords.edges.length === 0 &&
        listPartnerPayoutRecords.pageInfo.hasPreviousPage
      ) {
        setPagination(current => ({
          ...current,
          pageNumber: listPartnerPayoutRecords.pageInfo.numPages,
        }));
      }
    }
  };

  const handleManuallySettlePayout = async payoutIds => {
    if (!payoutIds.length) {
      return message.open({
        content: t('cms.referral.payout.action.manual_settle.no_payout_selected'),
        icon: <Icon type="info-circle" theme="filled" style={{ color: '#F5222D' }} />,
      });
    }
    const response = await manuallySettlePayout({
      variables: {
        payoutRecordIds: payoutIds,
        paymentPreference: selectedPaymentPreference,
      },
    });
    if (response.data?.bulkSettlePartnerPayout?.result) {
      message.success(t('cms.referral.payout.action.manual_settle.toast'));
    }
  };

  const handleRemoveFromSettlementList = async (selectedRowId, payoutList) => {
    const removeFromSettlementListResponse = await removeFromSettlementList({
      variables: {
        ids: payoutList
          .filter(payout => selectedRowId.includes(payout.id))
          .map(payout => payout.id),
      },
    });
    if (removeFromSettlementListResponse.data) {
      message.success(t('cms.referral.payout.action.unapporve.toast'));
      setSelectedRows([]);
      refetchAndResetPagination();
    }
  };

  const handleOnClose = () => {
    setIsVisitable(false);
    setTimeout(() => {
      onClose();
    }, 200);
  };

  useEffect(() => {
    const HeaderDom = document.querySelector('.dashboard-header');
    const BodyDom = document.querySelector('.payout-list__header');
    if (HeaderDom && BodyDom) {
      if (isVisitable) {
        HeaderDom.classList.add('dashboard-header-zindex-for-payout-settlement');
        BodyDom.classList.add('payout-list__header--header-fixed');
      } else {
        HeaderDom.classList.remove('dashboard-header-zindex-for-payout-settlement');
        BodyDom.classList.remove('payout-list__header--header-fixed');
      }
    }
    return () => {
      HeaderDom.classList.remove('dashboard-header-zindex-for-payout-settlement');
      BodyDom.classList.remove('payout-list__header--header-fixed');
    };
  }, [isVisitable]);

  const tableColumns = useMemo(() => {
    return [
      {
        title: t('cms.referral.payout.list.column.name'),
        dataIndex: 'partner.name',
        key: 'name',
        width: 140,
        className: 'payout-list__drawer__list__column',
        render: text => (
          <span title={text} className="payout-list__drawer__list__column-body">
            {text || '-'}
          </span>
        ),
        filterDropdown: (
          <TableAsyncSelect
            field="partnerId"
            loading={partnerListLoading}
            isMulti
            value={queryFilter.partnerId || []}
            list={partnerList}
            onInput={value => setSearchPartnerName(value)}
            fetching={partnerListLoading}
            onChange={(_fieldName, value) => {
              const partnerIds = value.length ? value : [];
              setQueryFilter(current => ({ ...current, partnerId: partnerIds }));
              setPagination(current => ({ ...current, pageNumber: 1 }));
              setShowPartnerIdFilter(false);
            }}
          />
        ),
        filterIcon: (
          <SearchOutlined style={{ color: queryFilter.partnerId.length ? '#38b2a6' : '#c8c9cb' }} />
        ),
        filterDropdownVisible: showPartnerIdFilter,
        onFilterDropdownVisibleChange: view => {
          setShowPartnerIdFilter(view);
        },
      },
      {
        title: (
          <div className="payout-list__body__list__column-header">
            {t('cms.referral.payout.list.column.payout_amount')}
          </div>
        ),
        dataIndex: 'amount',
        width: 120,
        key: 'payoutAmount',
        className: 'payout-list__drawer__list__column payout-list__drawer__list__column--pl-0',
        render: (text, record) => (
          <span className="payout-list__drawer__list__column-body">
            {text && record.currency ? `${formatPriceWithSpace(text, record.currency)}` : '-'}
          </span>
        ),
      },
    ];
  }, [showPartnerIdFilter, queryFilter, partnerList]);

  const handleSelectWalletCountry = (v, selectedOption) => {
    const selectedCountryCode = selectedOption.props.countryCode;
    const isSelectedPaymentPreferenceValid =
      PaymentPreferenceAction(selectedCountryCode)?.includes(selectedPaymentPreference);

    if (!isSelectedPaymentPreferenceValid) {
      setSelectedPaymentPreference(PaymentPreferenceAction(selectedCountryCode)?.[0] || 'ONLINE');
    }
    setSelectWalletCountry(v);
    setSelectWalletCurrency();
    cookies.set(cookieNames.payoutDefaultSettlementCountry, v);
    cookies.remove(cookieNames.payoutDefaultSettlementCurrency);
  };

  const selectedCountryPaymentPreference =
    PaymentPreferenceAction(currentSelectCountry?.countryCode) || [];
  return (
    <div
      className={classNames('payout-list__drawer', {
        'payout-list__drawer--visiable': isVisitable,
        'payout-list__drawer--hidden': !isVisitable,
      })}
    >
      <div className="payout-list__drawer__container">
        <div className="payout-list__drawer__header">
          <h3>{t('cms.referral.payout.drawer.title')}</h3>
          <CloseOutlined
            className="payout-list__drawer__header__close-button"
            onClick={handleOnClose}
          />
        </div>

        <div className="payout-list__drawer__body">
          <div className="payout-list__drawer__wallet-select">
            <Select
              className="payout-list__drawer__wallet-select__wallet-locale"
              value={selectWalletCountry}
              onSelect={handleSelectWalletCountry}
            >
              {availableCountries.map(country => (
                <Select.Option key={country.value} countryCode={country.countryCode}>
                  {country.label}
                </Select.Option>
              ))}
            </Select>
            <Select
              className="payout-list__drawer__wallet-select__wallet-currency"
              value={selectWalletCurrency}
              onChange={v => {
                setSelectWalletCurrency(v);
                cookies.set(cookieNames.payoutDefaultSettlementCurrency, v);
              }}
            >
              {availableCurrencies.map(currency => (
                <Select.Option key={currency.value}>{currency.label}</Select.Option>
              ))}
            </Select>
          </div>

          <Tabs
            className="payout-list__drawer__tabs"
            activeKey={selectedPaymentPreference}
            onChange={activeKey => {
              setSelectedPaymentPreference(activeKey);
            }}
          >
            <TabPane
              tab="Online"
              key="ONLINE"
              disabled={!selectedCountryPaymentPreference.includes('ONLINE')}
            ></TabPane>
            <TabPane
              tab="Offline"
              key="OFFLINE"
              disabled={!selectedCountryPaymentPreference.includes('OFFLINE')}
            ></TabPane>
          </Tabs>

          <div className="payout-list__drawer__intro">
            <span className="payout-list__drawer__intro__title">
              {t('cms.referral.payout.drawer.info.current_date', {
                month: moment().format('MMM'),
                year: moment().year(),
                country: currentSelectCountry?.label || '',
              })}
            </span>
            {isAutoSettlementCountry && (
              <span className="payout-list__drawer__intro__subTitle">
                {t('cms.referral.payout.drawer.info.next_date', {
                  v: settlementInfo?.nextSettleData
                    ? formatDay('Do MMM YYYY', settlementInfo?.nextSettleData)
                    : '-',
                })}
              </span>
            )}
          </div>

          <div
            className={classNames('payout-list__drawer__statistic', {
              'payout-list__drawer__statistic--manually': !isAutoSettlementCountry,
            })}
          >
            {isAutoSettlementCountry && (
              <>
                <Progress
                  strokeColor="#1890FF"
                  percent={settlementInfo?.approvedPercentage}
                  size="small"
                />
                <Row gutter={24}>
                  <Col span={8}>
                    <Statistic
                      title={t('cms.referral.payout.drawer.statistic.remaning')}
                      className={classNames('payout-list__drawer__statistic__item', {
                        'payout-list__drawer__statistic__item--primary':
                          settlementInfo?.remainingAmount,
                        'payout-list__drawer__statistic__item--warning':
                          settlementInfo?.remainingAmount === 0,
                      })}
                      value={
                        settlementInfo
                          ? formatPriceWithNoSymbol(settlementInfo.remainingAmount)
                          : '—'
                      }
                    />
                  </Col>

                  <Col span={8}>
                    <Statistic
                      className="payout-list__drawer__statistic__item"
                      title={
                        <div className="payout-list__drawer__statistic__item__title">
                          {t('cms.referral.payout.drawer.statistic.budget')}
                          <If condition={role.slug !== 'bd'}>
                            <PayoutSettlementSyncIcon
                              onClick={() => syncXHJTopop()}
                              className={classNames(
                                'payout-list__drawer__statistic__item__title__icon',
                                {
                                  'payout-list__drawer__statistic__item__title__icon--active':
                                    syncXHJTopopLoading || settlementInfoLoading,
                                },
                              )}
                            />
                          </If>
                        </div>
                      }
                      value={
                        settlementInfo ? formatPriceWithNoSymbol(settlementInfo.budgetAmount) : '—'
                      }
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      className="payout-list__drawer__statistic__item"
                      title={t('cms.referral.payout.drawer.statistic.approved')}
                      value={
                        settlementInfo
                          ? formatPriceWithNoSymbol(settlementInfo.approvedTotalAmount)
                          : '—'
                      }
                    />
                  </Col>
                </Row>
              </>
            )}

            {!isAutoSettlementCountry && (
              <>
                <Row gutter={24}>
                  <Col span={8}>
                    <Statistic
                      className="payout-list__drawer__statistic__item"
                      title={t('cms.referral.payout.drawer.statistic.approved')}
                      value={
                        settlementInfo
                          ? formatPriceWithNoSymbol(settlementInfo.approvedTotalAmount)
                          : '—'
                      }
                      suffix={
                        <div className="payout-list__drawer__statistic__manually-settle-btn-container">
                          <div className="payout-list__drawer__statistic__manually-settle-btn-divider" />
                          <Popconfirm
                            placement="bottom"
                            title={t('cms.referral.payout.drawer.statistic.settle.popover.title')}
                            overlayClassName="payout-list__drawer__statistic__manually-settle-popconfirm"
                            okText="Yes"
                            onConfirm={() => handleManuallySettlePayout(payoutList.map(p => p.id))}
                          >
                            <div>
                              <button className="payout-list__drawer__statistic__manually-settle-btn">
                                {t('cms.referral.payout.drawer.statistic.settle')}
                              </button>
                            </div>
                          </Popconfirm>
                        </div>
                      }
                    />
                  </Col>
                </Row>
              </>
            )}
          </div>

          <div className="payout-list__drawer__list">
            <Choose>
              <When condition={payoutListTotalCount || isValidFilter}>
                <Table
                  columns={tableColumns}
                  className={classNames('table-list payout-list__body__list')}
                  dataSource={payoutList}
                  size="middle"
                  rowKey={record => record.id}
                  {...(role.slug !== 'bd'
                    ? {
                        rowSelection: {
                          selectedRowKeys: selectedRows,
                          onChange: handleRowSelect,
                        },
                      }
                    : null)}
                  pagination={{
                    current: pagination.pageNumber,
                    pageSize: pagination.pageSize,
                    showSizeChanger: true,
                    hideOnSinglePage: true,
                    size: 'middle',
                    total: payoutListTotalCount,
                  }}
                  loading={loading}
                  scroll={{
                    y: totalPages > 5 ? 'calc(100vh - 490px)' : 'calc(100vh - 460px)',
                  }}
                  onChange={newestPagination => {
                    setPagination({
                      pageNumber: newestPagination.current,
                      pageSize: newestPagination.pageSize,
                    });
                  }}
                />
              </When>
              <When condition={payoutListTotalCount === 0 && !isValidFilter}>
                <div className="payout-list__drawer__blank-tips">
                  <div className="payout-list__drawer__blank-tips__content">
                    <Empty description={t('cms.referral.payout.drawer.blanktip')} />
                  </div>
                </div>
              </When>
            </Choose>
          </div>
        </div>
        <div className="payout-list__drawer__footer">
          <span className="payout-list__drawer__footer__sum">
            <If condition={payoutListTotalCount}>{payoutListTotalCount} Approved</If>
          </span>
          <button
            disabled={!selectedRows.length && role.slug !== 'bd'}
            onClick={() => handleRemoveFromSettlementList(selectedRows, payoutList)}
            className={classNames('payout-list__drawer__footer__btn', {
              'payout-list__drawer__footer__btn--disabled': !selectedRows.length,
              'payout-list__drawer__footer__btn--loading': removeFromSettlementListLoading,
            })}
          >
            <If condition={removeFromSettlementListLoading}>
              <Spin
                indicator={
                  <Icon
                    type="loading"
                    className="payout-list__drawer__footer__btn__icon"
                    style={{ fontSize: 14, color: 'white' }}
                    spin
                  />
                }
              />
            </If>
            {t('cms.referral.payout.drawer.statistic.remove')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SettlementList;
